import React, { useState } from 'react';
import {
  Drawer,
  Form,
  Select,
  Slider,
  Checkbox,
  Button,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Tag,
} from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;
const { Option } = Select;

interface AdvancedFiltersProps {
  visible: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterOptions) => void;
  initialFilters?: FilterOptions;
}

export interface FilterOptions {
  categories: string[];
  minRating: number;
  maxRating: number;
  priceRange: string[];
  certifications: string[];
  countries: string[];
  foundedYear: [number, number];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  visible,
  onClose,
  onApplyFilters,
  initialFilters,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [filters, setFilters] = useState<FilterOptions>(
    initialFilters || {
      categories: [],
      minRating: 0,
      maxRating: 5,
      priceRange: [],
      certifications: [],
      countries: [],
      foundedYear: [1900, 2024],
      sortBy: 'overall_score',
      sortOrder: 'desc',
    }
  );

  const categories = [
    '时尚',
    '化妆品',
    '食品',
    '家居',
    '电子产品',
    '运动用品',
    '珠宝',
    '汽车',
    '旅游',
    '金融服务',
  ];

  const priceRanges = [
    '经济实惠 (¥0-500)',
    '中等价位 (¥500-2000)',
    '高端 (¥2000-10000)',
    '奢侈品 (¥10000+)',
  ];

  const certifications = [
    'B Corp认证',
    'GOTS有机纺织品',
    'Fair Trade公平贸易',
    'Cradle to Cradle',
    'LEED绿色建筑',
    'Forest Stewardship Council',
    'Rainforest Alliance',
    'EPEAT环保电子产品',
  ];

  const countries = [
    '美国',
    '英国',
    '法国',
    '德国',
    '意大利',
    '日本',
    '韩国',
    '中国',
    '瑞典',
    '丹麦',
    '荷兰',
    '瑞士',
    '加拿大',
    '澳大利亚',
  ];

  const sortOptions = [
    { value: 'overall_score', label: '综合评分' },
    { value: 'environmental_score', label: '环境影响' },
    { value: 'labor_score', label: '劳工权益' },
    { value: 'animal_welfare_score', label: '动物福利' },
    { value: 'community_impact_score', label: '社区影响' },
    { value: 'transparency_score', label: '透明度' },
    { value: 'name', label: '品牌名称' },
    { value: 'founded', label: '成立时间' },
  ];

  const handleApplyFilters = () => {
    form.validateFields().then(values => {
      const newFilters = {
        ...filters,
        ...values,
        minRating: values.ratingRange[0],
        maxRating: values.ratingRange[1],
        foundedYear: values.foundedYear || [1900, 2024],
      };
      setFilters(newFilters);
      onApplyFilters(newFilters);
      onClose();
    });
  };

  const handleResetFilters = () => {
    const defaultFilters: FilterOptions = {
      categories: [],
      minRating: 0,
      maxRating: 5,
      priceRange: [],
      certifications: [],
      countries: [],
      foundedYear: [1900, 2024],
      sortBy: 'overall_score',
      sortOrder: 'desc',
    };
    setFilters(defaultFilters);
    form.setFieldsValue({
      categories: [],
      ratingRange: [0, 5],
      priceRange: [],
      certifications: [],
      countries: [],
      foundedYear: [1900, 2024],
      sortBy: 'overall_score',
      sortOrder: 'desc',
    });
    onApplyFilters(defaultFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.categories.length > 0) count++;
    if (filters.minRating > 0 || filters.maxRating < 5) count++;
    if (filters.priceRange.length > 0) count++;
    if (filters.certifications.length > 0) count++;
    if (filters.countries.length > 0) count++;
    if (filters.foundedYear[0] > 1900 || filters.foundedYear[1] < 2024) count++;
    return count;
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FilterOutlined />
          <span>{t('filters.title')}</span>
          {getActiveFiltersCount() > 0 && (
            <Tag color="blue">
              {t('filters.active_count', { count: getActiveFiltersCount() })}
            </Tag>
          )}
        </div>
      }
      placement="right"
      width={400}
      onClose={onClose}
      open={visible}
      footer={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button icon={<ClearOutlined />} onClick={handleResetFilters}>
            {t('filters.reset')}
          </Button>
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={handleApplyFilters}
          >
            {t('filters.apply')}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          categories: filters.categories,
          ratingRange: [filters.minRating, filters.maxRating],
          priceRange: filters.priceRange,
          certifications: filters.certifications,
          countries: filters.countries,
          foundedYear: filters.foundedYear,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
        }}
      >
        <Title level={5}>{t('filters.categories')}</Title>
        <Form.Item name="categories">
          <Checkbox.Group>
            <Row gutter={[8, 8]}>
              {categories.map(category => (
                <Col span={12} key={category}>
                  <Checkbox value={category}>{category}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </Form.Item>

        <Divider />

        <Title level={5}>{t('filters.rating_range')}</Title>
        <Form.Item name="ratingRange">
          <Slider
            range
            min={0}
            max={5}
            step={0.1}
            marks={{
              0: '0',
              1: '1',
              2: '2',
              3: '3',
              4: '4',
              5: '5',
            }}
            tooltip={{
              formatter: value => String(value),
            }}
          />
        </Form.Item>

        <Divider />

        <Title level={5}>{t('filters.price_range')}</Title>
        <Form.Item name="priceRange">
          <Checkbox.Group>
            <Space direction="vertical">
              {priceRanges.map(range => (
                <Checkbox key={range} value={range}>
                  {range}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Form.Item>

        <Divider />

        <Title level={5}>{t('filters.certifications')}</Title>
        <Form.Item name="certifications">
          <Checkbox.Group>
            <Space direction="vertical">
              {certifications.map(cert => (
                <Checkbox key={cert} value={cert}>
                  {cert}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Form.Item>

        <Divider />

        <Title level={5}>{t('filters.countries')}</Title>
        <Form.Item name="countries">
          <Select
            mode="multiple"
            placeholder={t('filters.country_placeholder')}
            style={{ width: '100%' }}
            maxTagCount={3}
          >
            {countries.map(country => (
              <Option key={country} value={country}>
                {country}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Divider />

        <Title level={5}>{t('filters.founded_year')}</Title>
        <Form.Item name="foundedYear">
          <Slider
            range
            min={1900}
            max={2024}
            marks={{
              1900: '1900',
              1950: '1950',
              2000: '2000',
              2024: '2024',
            }}
            tooltip={{
              formatter: value => String(value),
            }}
          />
        </Form.Item>

        <Divider />

        <Title level={5}>{t('filters.sorting')}</Title>
        <Row gutter={16}>
          <Col span={16}>
            <Form.Item name="sortBy" label={t('filters.sort_by')}>
              <Select>
                {sortOptions.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="sortOrder" label={t('filters.order')}>
              <Select>
                <Option value="desc">{t('filters.desc')}</Option>
                <Option value="asc">{t('filters.asc')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
};

export default AdvancedFilters;
